import { CELL } from './constants';
import { cx, cy, key } from './geometry';
import { network } from './network';
import { mstEdges } from './scoring';
import type { Point, Puzzle, ScoredPuzzle } from './types';

/**
 * The named shape of a reference layout (GDD §3.5).
 *
 * The name is stamped on the perfect card and in the share, so it is a pure
 * function of `(puzzle, towers)` and nothing else: the same reference gets the
 * same name on every device, forever.
 */
export type Topology = 'spine' | 'y-split' | 'backbranch' | 'river-kiss' | 'crossbar';

/**
 * What the player reads. Lower case and definite, the way the share sets it
 * ("🏆 Superconductor · the river kiss") — a name for a thing, not a title.
 */
export const TOPOLOGY_NAME: Record<Topology, string> = {
  spine: 'the spine',
  'y-split': 'the Y-split',
  backbranch: 'the backbranch',
  'river-kiss': 'the river kiss',
  crossbar: 'the crossbar',
};

/**
 * How close a pass sits to the corridor to count as a kiss, in cells.
 *
 * Half a cell is "the wire crosses inside the corridor's own cell": the ghost
 * corridor (GDD §3.4) is drawn as a zero-width line, and a crossing within
 * half a cell of it is on the same square of the map. That is the minimum
 * offset the board can express — the reference took the corridor's own way
 * over rather than walking the bank for a better one.
 */
const KISS_CELLS = 0.5;

/**
 * How far apart two passes have to be to count as separate, in cells.
 *
 * A wide barrier can take two hops to clear, and two hops over the same water
 * are one way through, not two. Two cells is the span rule's own reach: passes
 * closer than that are the same gap in the terrain, seen twice.
 */
const SEPARATE_PASS_CELLS = 2;

/**
 * Samples per line when asking what it flies over. Matches the sampling
 * `network()` uses for the crossing-giant role, so the two agree about which
 * spans are over terrain.
 */
const TERRAIN_SAMPLES = 32;

/** A point in board units, the units terrain and the corridor are measured in. */
interface Vec {
  x: number;
  y: number;
}

/** One live line of the reference tree, and where (if anywhere) it flies over terrain. */
interface Line {
  /** Endpoint indices into {@link Tree.nodes}. */
  a: number;
  b: number;
  /**
   * Midpoint of the stretch that flies over blocked terrain, in board units,
   * or `null` where the line stays over open land. A line with a pass is a way
   * through the terrain, and most of this taxonomy is about where those sit.
   */
  pass: Vec | null;
}

/** The reference layout as the graph the taxonomy reads. */
interface Tree {
  /** The plant first, then the energized towers, then the lit houses. */
  nodes: Point[];
  /** How many of {@link Tree.nodes} are nodes rather than houses; houses follow. */
  trunk: number;
  /** Live spans between nodes, plus each lit house's drop. */
  lines: Line[];
  /**
   * Live lines at each node, house drops excluded — see {@link referenceTree}
   * for why the drops are left out.
   */
  degree: number[];
}

/** Does the straight line between two cells fly over blocked terrain, and where? */
function passPoint(puzzle: Puzzle, p: Point, q: Point): Vec | null {
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (let t = 1; t < TERRAIN_SAMPLES; t++) {
    const f = t / TERRAIN_SAMPLES;
    const x = cx(p) + (cx(q) - cx(p)) * f;
    const y = cy(p) + (cy(q) - cy(p)) * f;
    if (puzzle.obstacles[key({ r: Math.floor(y / CELL), c: Math.floor(x / CELL) })]) {
      sx += x;
      sy += y;
      n++;
    }
  }
  // Water and mountains both count. A pass is a way through terrain a tower
  // can't stand on, and which kind it is names the archetype, not the shape.
  return n ? { x: sx / n, y: sy / n } : null;
}

/**
 * The reference layout as a graph: what carries power, and what to what.
 *
 * Dark towers and dead lines are left out — an unpowered tower is wasted wire,
 * not part of the shape. Houses come in as leaves, because a drop over water
 * is a way through the terrain like any other span.
 *
 * **Degree counts spans, not drops**, and that is what makes the spine
 * reachable. The game's own vocabulary separates the two: a *node* is the
 * plant or a tower, a *line* runs between two nodes within span, and a house
 * takes "exactly one drop" (GDD §3.2, §3.4). A reference has four terminals —
 * the plant and three houses — so a tree that counted drops as lines would
 * always branch somewhere, and "a pure path" would name nothing: measured over
 * 200 consecutive grids, counting drops makes every one of the 200 references
 * branch, while counting spans leaves the fork on about a quarter of them.
 * Counting spans, the names describe the shape of the *line*: a spine is one
 * run of towers with houses hanging off it, a Y-split is a run that forks.
 *
 * This deliberately reads "three or more lines" differently from
 * `towerRoles()`, which counts drops when it picks the cat-face sprite. That
 * asks a physical question — how many wires does this tower actually carry —
 * and its answer is right for the drawing. This asks what shape the line is.
 */
function referenceTree(puzzle: Puzzle, towers: Point[]): Tree {
  const net = network(puzzle, towers);

  const at = net.nodes.map(() => -1);
  const nodes: Point[] = [];
  for (let i = 0; i < net.nodes.length; i++) {
    if (net.energized.has(i)) {
      at[i] = nodes.length;
      nodes.push(net.nodes[i]);
    }
  }
  const trunk = nodes.length;

  const lines: Line[] = [];
  const degree = nodes.map(() => 0);
  for (const e of net.edges) {
    if (!e.live) continue;
    lines.push({ a: at[e.ai], b: at[e.bi], pass: passPoint(puzzle, e.a, e.b) });
    degree[at[e.ai]]++;
    degree[at[e.bi]]++;
  }
  for (const hl of net.houseLinks) {
    const node = hl.node;
    if (!hl.lit || node === null || hl.nodeIdx === null) continue;
    lines.push({ a: at[hl.nodeIdx], b: nodes.length, pass: passPoint(puzzle, node, hl.h) });
    nodes.push(hl.h);
    degree.push(0);
  }

  return { nodes, trunk, lines, degree };
}

/**
 * Connected components of the graph with something taken away: every line
 * `keep` rejects is cut, and `dropNode` (when given) is removed outright.
 *
 * Cutting and asking what fell apart is what keeps the predicates honest on a
 * layout that isn't strictly a tree. Every pair of nodes within span is wired
 * whether the player wanted it or not (GDD §3.2), so a reference can carry a
 * redundant edge and a cycle with it, and "which side of the water" has to be
 * a question about separation rather than about the one path between two
 * nodes.
 *
 * Returns a component id per node, and -1 for the dropped one.
 */
function componentsOf(tree: Tree, keep: (line: Line) => boolean, dropNode = -1): number[] {
  const comp: number[] = tree.nodes.map((_n, i) => (i === dropNode ? -1 : -2));
  const live = tree.lines.filter((l) => keep(l) && l.a !== dropNode && l.b !== dropNode);
  let next = 0;
  for (let seed = 0; seed < comp.length; seed++) {
    if (comp[seed] !== -2) continue;
    const id = next++;
    comp[seed] = id;
    const queue = [seed];
    while (queue.length) {
      const n = queue.pop() ?? 0;
      for (const l of live) {
        const other = l.a === n ? l.b : l.b === n ? l.a : -1;
        if (other >= 0 && comp[other] === -2) {
          comp[other] = id;
          queue.push(other);
        }
      }
    }
  }
  return comp;
}

/** Which side of a cut a line ended up on, taking whichever endpoint survived it. */
function sideOf(comp: number[], line: Line): number {
  return comp[line.a] >= 0 ? comp[line.a] : comp[line.b];
}

const gap = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * The `wireFloor` corridor as segments, in board units.
 *
 * Deliberately the same edges the board draws its ghost corridor from (GDD
 * §3.4) rather than a private MST walk: the river kiss is defined against the
 * line the player can see, so a second implementation that only agreed by
 * construction would be one edit away from naming a kiss the board doesn't
 * show.
 */
function corridor(puzzle: Puzzle): { a: Vec; b: Vec }[] {
  return mstEdges(puzzle).map(([a, b]) => ({
    a: { x: cx(a), y: cy(a) },
    b: { x: cx(b), y: cy(b) },
  }));
}

/** Shortest distance from a point to a segment, in board units. */
function toSegment(p: Vec, a: Vec, b: Vec): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/**
 * A junction with two separate passes on opposite sides of it — "a junction
 * sitting between two separate passes through the terrain".
 *
 * Measured by removing the junction and seeing whether the two passes fall
 * into different components, so it holds whether the junction sits between the
 * crossings on one line or fans two crossings out from one bank.
 */
function isCrossbar(tree: Tree, junctions: number[], passes: (Line & { pass: Vec })[]): boolean {
  for (const j of junctions) {
    const comp = componentsOf(tree, () => true, j);
    for (let i = 0; i < passes.length; i++) {
      for (let k = i + 1; k < passes.length; k++) {
        if (gap(passes[i].pass, passes[k].pass) < SEPARATE_PASS_CELLS * CELL) continue;
        const si = sideOf(comp, passes[i]);
        const sk = sideOf(comp, passes[k]);
        if (si >= 0 && sk >= 0 && si !== sk) return true;
      }
    }
  }
  return false;
}

/**
 * A junction on the plant's side of the terrain with a pass beyond it — "the
 * line splits before it crosses".
 *
 * Near side means: cut every pass, and the junction is still attached to the
 * plant. Beyond it means: cut the junction out, and some pass is no longer
 * attached to the plant either. Both halves are needed — a fork near the plant
 * that the crossing never passes through is a fork somewhere else on the map,
 * not a branch before the crossing.
 */
function isBackbranch(tree: Tree, junctions: number[], passes: (Line & { pass: Vec })[]): boolean {
  const nearSide = componentsOf(tree, (l) => l.pass === null);
  for (const j of junctions) {
    if (nearSide[j] !== nearSide[0]) continue;
    // The plant branching is before every crossing by construction: there is
    // nothing upstream of it to cut.
    if (j === 0) return true;
    const comp = componentsOf(tree, () => true, j);
    if (passes.some((p) => sideOf(comp, p) >= 0 && sideOf(comp, p) !== comp[0])) return true;
  }
  return false;
}

/**
 * Name the shape of a reference layout (GDD §3.5).
 *
 * The five names, each as a predicate on the live graph — the plant, every
 * energized tower, every lit house, and the wire between them. Two terms carry
 * the whole taxonomy:
 *
 * - a **junction** is a node carrying three or more live lines, house drops
 *   excluded (see {@link referenceTree}): the point where the line of towers
 *   forks;
 * - a **pass** is a line that flies over blocked terrain: one way through.
 *
 * A layout can satisfy several predicates, so the order they are tested in is
 * part of the definition. **Branch structure is named before the crossing.**
 * The skeleton is what a player redraws when they change their answer, and
 * where it happens to cross is a fact about that skeleton, so the layouts that
 * fork are split three ways and the layouts that don't are split two:
 *
 * ```
 * does the line fork?
 *   yes → the crossbar | the backbranch | the Y-split
 *   no  → the river kiss | the spine
 * ```
 *
 * 1. **the crossbar** — a junction with two separate passes on opposite sides
 *    of it: cut the junction out and the two passes fall into different
 *    components. Separate means their crossing points sit at least
 *    {@link SEPARATE_PASS_CELLS} apart, so one wide barrier cleared in two
 *    hops stays one pass. Two ways through the terrain, joined at a junction.
 * 2. **the backbranch** — a junction on the plant's side of the terrain with a
 *    pass beyond it. The line splits before it crosses.
 * 3. **the Y-split** — the line forks and the terrain is not the story: one
 *    junction, three arms. Also the honest fallback for any branching shape
 *    the first two don't name.
 * 4. **the river kiss** — an unbranched line whose pass sits within
 *    {@link KISS_CELLS} of the corridor. The wire crosses where the corridor
 *    itself crosses: no walk along the bank was worth taking.
 * 5. **the spine** — an unbranched line with nothing else to say: a pure path.
 *    The fallback, and the reason the function is total.
 */
export function classifyTopology(puzzle: ScoredPuzzle | Puzzle, towers: Point[]): Topology {
  const tree = referenceTree(puzzle, towers);
  const junctions: number[] = [];
  for (let i = 0; i < tree.trunk; i++) if (tree.degree[i] >= 3) junctions.push(i);
  const passes = tree.lines.filter((l): l is Line & { pass: Vec } => l.pass !== null);

  if (junctions.length) {
    if (passes.length > 1 && isCrossbar(tree, junctions, passes)) return 'crossbar';
    if (passes.length && isBackbranch(tree, junctions, passes)) return 'backbranch';
    return 'y-split';
  }

  if (passes.length) {
    const segments = corridor(puzzle);
    const offset = Math.min(
      ...passes.map((p) => Math.min(...segments.map((s) => toSegment(p.pass, s.a, s.b)))),
    );
    if (offset <= KISS_CELLS * CELL) return 'river-kiss';
  }
  return 'spine';
}
